function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatInventoryNumber(value) {
  const parsed = toNumber(value);
  if (parsed === null) return "";
  if (Math.abs(parsed - Math.round(parsed)) < 0.0001) {
    return String(Math.round(parsed));
  }
  return parsed.toFixed(2).replace(/\.?0+$/, "");
}

export function getPurchaseUnitLabel(item) {
  const purchaseUnitQuantity = toNumber(item?.purchase_unit_quantity);
  const packageName = (item?.purchase_unit_name || "").trim();
  const unit = (item?.unit || "").trim();

  if (!purchaseUnitQuantity) {
    return packageName;
  }

  const baseLabel = `${formatInventoryNumber(purchaseUnitQuantity)} ${unit}`.trim();
  return packageName ? `${baseLabel} / ${packageName}` : baseLabel;
}

export function getDerivedCostPerUnit(item) {
  const purchaseUnitQuantity = toNumber(item?.purchase_unit_quantity);
  const purchaseUnitCost = toNumber(item?.purchase_unit_cost);

  if (purchaseUnitQuantity && purchaseUnitCost !== null) {
    return purchaseUnitCost / purchaseUnitQuantity;
  }

  return toNumber(item?.cost_per_unit);
}

export function normalizeInventoryItemForm(item) {
  const purchaseUnitQuantity =
    item?.purchase_unit_quantity != null
      ? formatInventoryNumber(item.purchase_unit_quantity)
      : item?.cost_per_unit != null
        ? "1"
        : "";

  const purchaseUnitCost =
    item?.purchase_unit_cost != null
      ? formatInventoryNumber(item.purchase_unit_cost)
      : item?.cost_per_unit != null
        ? formatInventoryNumber(item.cost_per_unit)
        : "";

  return {
    name: item?.name || "",
    article_number: item?.article_number || "",
    current_quantity: item?.current_quantity != null ? formatInventoryNumber(item.current_quantity) : "",
    unit: item?.unit || "kg",
    purchase_unit_quantity: purchaseUnitQuantity,
    purchase_unit_name: item?.purchase_unit_name || "",
    minimum_quantity: item?.minimum_quantity != null ? formatInventoryNumber(item.minimum_quantity) : "",
    reorder_point: item?.reorder_point != null ? formatInventoryNumber(item.reorder_point) : "",
    supplier_id: item?.supplier_id != null ? String(item.supplier_id) : "",
    supplier: item?.supplier || "",
    lead_time_days: item?.lead_time_days != null ? String(item.lead_time_days) : "",
    category: item?.category || "Frische Zutaten",
    purchase_unit_cost: purchaseUnitCost,
  };
}

export function buildInventoryItemPayload(form) {
  const currentQuantity = toNumber(form.current_quantity);
  const minimumQuantity = toNumber(form.minimum_quantity);
  const reorderPoint = toNumber(form.reorder_point);
  const leadTimeDays = toNumber(form.lead_time_days);
  const purchaseUnitQuantity = toNumber(form.purchase_unit_quantity);
  const purchaseUnitCost = toNumber(form.purchase_unit_cost);
  const supplierId =
    form.supplier_id === "" || form.supplier_id == null
      ? null
      : toNumber(form.supplier_id);
  const derivedCostPerUnit =
    purchaseUnitQuantity && purchaseUnitCost !== null
      ? purchaseUnitCost / purchaseUnitQuantity
      : purchaseUnitCost;

  return {
    ...form,
    article_number: form.article_number?.trim() || "",
    current_quantity: currentQuantity,
    minimum_quantity: minimumQuantity,
    reorder_point: reorderPoint,
    lead_time_days: leadTimeDays !== null ? Math.round(leadTimeDays) : undefined,
    purchase_unit_quantity: purchaseUnitQuantity || undefined,
    purchase_unit_name: form.purchase_unit_name?.trim() || "",
    purchase_unit_cost: purchaseUnitCost !== null ? purchaseUnitCost : undefined,
    supplier_id: supplierId !== null ? supplierId : null,
    supplier: form.supplier?.trim() || "",
    cost_per_unit: derivedCostPerUnit !== null ? Number(derivedCostPerUnit.toFixed(2)) : undefined,
  };
}

export function getPurchasePlan(item, requiredQuantity) {
  const neededQuantity = Math.max(0, toNumber(requiredQuantity) || 0);
  const explicitPurchaseUnitQuantity = toNumber(item?.purchase_unit_quantity);
  const purchaseUnitQuantity = explicitPurchaseUnitQuantity && explicitPurchaseUnitQuantity > 0
    ? explicitPurchaseUnitQuantity
    : null;
  const purchaseUnitCost = toNumber(item?.purchase_unit_cost);
  const derivedUnitCost = getDerivedCostPerUnit(item);
  const packageLabel = getPurchaseUnitLabel(item);

  if (!purchaseUnitQuantity) {
    return {
      hasPurchaseConfig: false,
      coverageQuantity: neededQuantity,
      purchaseUnitsNeeded: null,
      purchaseUnitCost,
      estimatedCost: derivedUnitCost !== null ? neededQuantity * derivedUnitCost : 0,
      packageLabel,
      summary: null,
    };
  }

  const purchaseUnitsNeeded = neededQuantity > 0
    ? Math.max(1, Math.ceil(neededQuantity / purchaseUnitQuantity))
    : 1;
  const coverageQuantity = purchaseUnitsNeeded * purchaseUnitQuantity;
  const estimatedCost = purchaseUnitCost !== null
    ? purchaseUnitsNeeded * purchaseUnitCost
    : derivedUnitCost !== null
      ? coverageQuantity * derivedUnitCost
      : 0;

  return {
    hasPurchaseConfig: true,
    purchaseUnitQuantity,
    purchaseUnitCost,
    purchaseUnitsNeeded,
    coverageQuantity,
    estimatedCost,
    packageLabel,
    summary: `${formatInventoryNumber(purchaseUnitsNeeded)} x ${packageLabel || `${formatInventoryNumber(purchaseUnitQuantity)} ${item?.unit || ""}`.trim()}`.trim(),
  };
}

export function getOrderDraft(item, enteredAmount) {
  const enteredValue = Math.max(0, toNumber(enteredAmount) || 0);
  const purchasePlan = getPurchasePlan(item, enteredValue);

  if (!purchasePlan.hasPurchaseConfig) {
    return {
      usesPurchaseUnits: false,
      enteredAmount: enteredValue,
      purchaseUnitsOrdered: null,
      coverageQuantity: enteredValue,
      estimatedCost: purchasePlan.estimatedCost,
      packageLabel: purchasePlan.packageLabel,
      summary: enteredValue > 0 ? `${formatInventoryNumber(enteredValue)} ${item?.unit || ""}`.trim() : "",
    };
  }

  const purchaseUnitsOrdered = enteredValue;
  const coverageQuantity = purchaseUnitsOrdered * purchasePlan.purchaseUnitQuantity;
  const estimatedCost = purchasePlan.purchaseUnitCost != null
    ? purchaseUnitsOrdered * purchasePlan.purchaseUnitCost
    : purchasePlan.estimatedCost;

  return {
    usesPurchaseUnits: true,
    enteredAmount: purchaseUnitsOrdered,
    purchaseUnitsOrdered,
    coverageQuantity,
    estimatedCost,
    packageLabel: purchasePlan.packageLabel,
    summary: `${formatInventoryNumber(purchaseUnitsOrdered)} x ${purchasePlan.packageLabel || ''}`.trim(),
  };
}
